import { useState, useRef, useCallback, useEffect } from 'react'
import { decodeIdentity } from '@/lib/jwtAuth'
import { useAppStore } from '@/store/app'
import { iamService } from '@/api/iam'
import type { PortalSettings } from '@/api/portalSettings'

// Direct Access Grant counterpart to useKeycloakAuth — used on the per-org
// portal subdomain so the login screen itself can be fully custom/branded
// instead of redirecting to Keycloak's hosted theme.
// Separate, more restricted Keycloak client with Direct Access Grants enabled
// — kept distinct from the staff SPA client (multiaccessclient) so enabling
// this grant type doesn't widen the attack surface of the staff login too.
const PORTAL_CLIENT_ID = import.meta.env.VITE_PORTAL_CLIENT_ID ?? 'portalclient'
const API_BASE  = import.meta.env.VITE_API_URL ?? ''
// The browser can't call Keycloak's token endpoint directly cross-origin
// from the portal subdomain — Keycloak's CORS support there is unreliable
// (preflight gets Access-Control-Allow-Origin, the actual response often
// doesn't, regardless of Web Origins config). The backend proxies the
// exchange server-to-server instead; see ProxyPortalToken in the Go backend.
const TOKEN_URL = `${API_BASE}/api/v1/public/portal/token`

const REFRESH_INTERVAL_MS = 60_000
const REFRESH_TOKEN_KEY   = 'portal_refresh_token'

export type PortalPublicBranding = Pick<PortalSettings, 'branding' | 'theme' | 'templates' | 'allowAttachments' | 'showOrgRequests' | 'requireLogin' | 'chatBeforeTicketEnabled' | 'chatWithAgentEnabled'>

interface PortalAuthState {
  ready:    boolean   // public branding fetch + session-resume attempt finished
  token:    string | null
  orgId:    string | null
  branding: PortalPublicBranding | null
  error:    string | null
}

interface TokenResponse {
  access_token:  string
  refresh_token: string
  organization_id: string
}

async function exchangeToken(body: URLSearchParams): Promise<TokenResponse> {
  const res = await fetch(TOKEN_URL, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error_description ?? 'Invalid username or password')
  return data
}

export function usePortalAuth(slug: string) {
  const { setAuth, updateCurrentUser } = useAppStore()
  const [state, setState] = useState<PortalAuthState>({ ready: false, token: null, orgId: null, branding: null, error: null })
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const applyToken = useCallback((accessToken: string, refreshToken: string, organizationId: string) => {
    // IAM resolves the application org ID; token claim keys may be array
    // indexes and nested IDs may be Keycloak UUIDs instead.
    const orgId = typeof organizationId === 'string' ? organizationId.trim() : ''
    if (!orgId || orgId === '0') throw new Error('Unable to resolve your organization. Please sign in again.')
    const identity = decodeIdentity(accessToken)
    sessionStorage.setItem('token', accessToken)
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
    sessionStorage.setItem('organizationId', orgId)
    setAuth({
      id: identity.id, name: identity.name, email: identity.email,
      role: 'viewer', roleName: identity.roleName,
      orgId, createdAt: '', updatedAt: '',
    }, accessToken)
    // Same as staff's KeycloakGate — the JWT's realm roles are a coarse flag,
    // not this app's real custom role list (client_individual, client_org_user, etc).
    iamService.getCurrentUser()
      .then(profile => {
        if ((profile.roleName ?? []).length > 0) updateCurrentUser({ roleName: profile.roleName })
        // client_org_user's ticket visibility/creation is scoped by client —
        // the backend reads this from a "clientname" header (see
        // getAuthHeaders() in client.ts), since there's no client_id claim on
        // the portal's Direct Grant token to pull it from directly.
        if (profile.clientName) sessionStorage.setItem('clientName', profile.clientName)
        else sessionStorage.removeItem('clientName')
      })
      .catch(() => undefined)
    setState(s => ({ ...s, token: accessToken, orgId, error: null }))
  }, [setAuth, updateCurrentUser])

  const logout = useCallback(() => {
    sessionStorage.removeItem('token')
    sessionStorage.removeItem('organizationId')
    sessionStorage.removeItem('clientName')
    sessionStorage.removeItem(REFRESH_TOKEN_KEY)
    setState(s => ({ ...s, token: null, orgId: null }))
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    setState(s => ({ ...s, error: null }))
    try {
      const data = await exchangeToken(new URLSearchParams({
        grant_type: 'password', client_id: PORTAL_CLIENT_ID, username, password, scope: 'openid email profile organization',
      }))
      applyToken(data.access_token, data.refresh_token, data.organization_id)
    } catch (e: any) {
      setState(s => ({ ...s, error: e.message ?? 'Login failed' }))
      throw e
    }
  }, [applyToken])

  // Fetch public branding for the pre-login screen, and try to resume an
  // existing session from a stored refresh token.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      let branding: PortalPublicBranding | null = null
      // No slug on the bare portal domain — no org to pre-fetch branding for,
      // the login screen just uses the generic defaults.
      if (slug) {
        try {
          const res = await fetch(`${API_BASE}/api/v1/public/portal/config?slug=${encodeURIComponent(slug)}`)
          if (res.ok) branding = await res.json()
        } catch { /* fall through to default/no branding */ }
      }
      if (cancelled) return
      setState(s => ({ ...s, branding, ready: true }))

      const storedRefresh = sessionStorage.getItem(REFRESH_TOKEN_KEY)
      if (storedRefresh) {
        try {
          const data = await exchangeToken(new URLSearchParams({
            grant_type: 'refresh_token', client_id: PORTAL_CLIENT_ID, refresh_token: storedRefresh, scope: 'openid email profile organization',
          }))
          if (!cancelled) applyToken(data.access_token, data.refresh_token, data.organization_id)
        } catch {
          if (!cancelled) logout()
        }
      }
    })()
    return () => { cancelled = true }
  }, [slug, applyToken, logout])

  // Keep the token fresh — same 60s-interval shape as useKeycloakAuth.
  useEffect(() => {
    if (!state.token) return
    intervalRef.current = setInterval(async () => {
      const storedRefresh = sessionStorage.getItem(REFRESH_TOKEN_KEY)
      if (!storedRefresh) return
      try {
        const data = await exchangeToken(new URLSearchParams({
          grant_type: 'refresh_token', client_id: PORTAL_CLIENT_ID, refresh_token: storedRefresh, scope: 'openid email profile organization',
        }))
        applyToken(data.access_token, data.refresh_token, data.organization_id)
      } catch {
        logout()
      }
    }, REFRESH_INTERVAL_MS)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [state.token, applyToken, logout])

  return { ...state, login, logout }
}
